import type { DestinationPlan } from './places/destinations';
import { ago, type RouteView, type TrackView } from './view';

/** A stop within this distance names the boat's position ("near Port Vila"); further out, the country does. */
const NEAR_NM = 15;
const DAY = 86_400_000;

/** Whole months from leaving the first port to reaching the last. */
export const tripMonths = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY / 30.44);

/** How a country reads in a sentence: Micronesia's states count once, and some names take "the". */
function inSentence(p: DestinationPlan): string {
  if (p.dest.country === 'Federated States of Micronesia') return 'Micronesia';
  return { solomons: 'the Solomon Islands', philippines: 'the Philippines' }[p.dest.slug] ?? p.dest.name;
}

const joinList = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** The first five countries between the start and the finish, for "by way of …". */
export function byWayOf(pages: DestinationPlan[]): string {
  return joinList([...new Set(pages.slice(1, -1).map(inSentence))].slice(0, 5));
}

const nm = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => Math.hypot((a.lon - b.lon) * Math.cos((a.lat * Math.PI) / 180), a.lat - b.lat) * 60;

/**
 * The "Right now" card over the photo. A quiet tracker is the normal case (nothing aboard is always on),
 * so it shows where COS last reported from and how long ago, not an alarm.
 */
export function whereNow(v: TrackView, stops: RouteView['stops'], now: number): { label: string; text: string; sub: string } {
  const last = v.last;
  if (!last || v.state === 'no-data') return { label: 'Right now', text: 'No position yet', sub: '' };
  const near = stops.map(s => ({ s, d: nm(s, last) })).filter(x => x.d <= NEAR_NM).sort((a, b) => a.d - b.d)[0]?.s ?? null;
  const place = near ? `near ${near.label}` : v.region ? `in ${v.region}` : 'at sea';
  const sub = [near ? v.region : null, v.state === 'underway' && last.sog !== null ? `${last.sog.toFixed(1)} kn` : null, ago(now - last.t)].filter(Boolean).join(' · ');
  if (v.state === 'silent') return { label: 'Last reported', text: place[0]!.toUpperCase() + place.slice(1), sub };
  return { label: 'Right now', text: `${v.state === 'underway' ? 'Under way' : 'At anchor'} ${place}`, sub };
}
